import { useCallback, useEffect, useState } from "react";
import { adminBroadcast, adminBroadcasts, adminListTemplates, api } from "@/lib/api";
import { toast } from "sonner";
import AutomationsPanel from "./AutomationsPanel";
import AnalyticsPanel from "./AnalyticsPanel";
import HomilyDigestPanel from "./HomilyDigestPanel";
import { Link } from "react-router-dom";

const TEMPLATES = {
  blank: { subject: "", html: "" },
  announcement: {
    subject: "An update from the Colorado Catholic Conference",
    html: "<h2 style='font-family:Georgia,serif;color:#3B0764;'>An update from the CCC</h2><p>Dear friend,</p><p>[Write your update here.]</p><p>— Colorado Catholic Conference</p>",
  },
  alert: {
    subject: "Take Action: [Bill Number]",
    html: "<h2 style='font-family:Georgia,serif;color:#3B0764;'>Take Action</h2><p>The Colorado Catholic Conference is asking Catholics across the state to contact their legislators today.</p><p><a href='https://www.cocatholic.org/take-action/action-alerts' style='color:#6B21A8;'>Take Action Now</a></p>",
  },
  event: {
    subject: "Join us: [Event Name]",
    html: "<h2 style='font-family:Georgia,serif;color:#3B0764;'>You're Invited</h2><p>Please join us for [event name] on [date] at [location].</p>",
  },
};

function BroadcastComposer({ form, setForm, savedTemplates }) {
  const applyTemplate = (k) => setForm({ ...form, ...TEMPLATES[k], template_id: null });
  const applySavedTemplate = (tpl) => setForm({
    ...form, subject: tpl.subject, html: tpl.html, template_id: tpl.id,
  });
  return (
    <div className="lg:col-span-2 bg-white border border-stone-200 p-6">
      <div className="mb-3">
        <div className="text-xs uppercase tracking-wider text-stone-500 mb-2">Quick starts</div>
        <div className="flex gap-2 flex-wrap">
          {Object.keys(TEMPLATES).map(k => (
            <button key={k} onClick={() => applyTemplate(k)}
              className="text-xs uppercase tracking-wider px-3 py-1 border border-stone-300 hover:border-ccc-purple">
              {k}
            </button>
          ))}
        </div>
      </div>
      {savedTemplates?.length > 0 && (
        <div className="mb-4">
          <div className="text-xs uppercase tracking-wider text-stone-500 mb-2 flex items-center justify-between">
            <span>Saved templates</span>
            <Link to="/admin/email-templates" className="text-ccc-purple hover:underline text-[10px]">Manage →</Link>
          </div>
          <select
            value={form.template_id || ""}
            onChange={e => {
              const tpl = savedTemplates.find(t => t.id === e.target.value);
              if (tpl) applySavedTemplate(tpl);
            }}
            data-testid="broadcast-template-picker"
            className="w-full px-3 py-2 border border-stone-300 text-sm"
          >
            <option value="">— Start from a saved template —</option>
            {savedTemplates.map(t => (
              <option key={t.id} value={t.id}>
                {t.name} {t.category ? `· ${t.category}` : ""} {t.detected_vars?.length ? `· vars: ${t.detected_vars.join(", ")}` : ""}
              </option>
            ))}
          </select>
        </div>
      )}
      <input placeholder="Subject" value={form.subject} onChange={e => setForm({...form, subject: e.target.value})}
        data-testid="email-subject"
        className="w-full px-3 py-2 border border-stone-300 mb-3 focus:outline-none focus:border-ccc-purple"/>
      <div className="mb-3">
        <label className="text-xs uppercase tracking-wider text-stone-500 flex items-center gap-1">
          <input type="checkbox" data-testid="ab-toggle"
            checked={form.ab_enabled || false}
            onChange={e => setForm({...form, ab_enabled: e.target.checked, subject_b: e.target.checked ? form.subject_b : ""})}
            className="accent-ccc-purple"/>
          A/B test subject lines (splits recipients 50/50)
        </label>
        {form.ab_enabled && (
          <input placeholder="Subject B (alternate subject line)"
            value={form.subject_b || ""} onChange={e => setForm({...form, subject_b: e.target.value})}
            data-testid="email-subject-b"
            className="w-full px-3 py-2 border border-stone-300 mt-2 focus:outline-none focus:border-ccc-purple"/>
        )}
      </div>
      <textarea rows={14} placeholder="HTML body — use {{first_name}}, {{parish}}, etc." value={form.html}
        onChange={e => setForm({...form, html: e.target.value})}
        data-testid="email-body"
        className="w-full px-3 py-2 border border-stone-300 font-mono text-xs focus:outline-none focus:border-ccc-purple"/>
      <div className="mt-2 text-[11px] text-stone-500">
        Available variables: <code>{"{{first_name}}"}</code>, <code>{"{{last_name}}"}</code>, <code>{"{{full_name}}"}</code>, <code>{"{{parish}}"}</code>, <code>{"{{diocese}}"}</code>, <code>{"{{email}}"}</code>, <code>{"{{unsubscribe_url}}"}</code>. Rendered per recipient.
      </div>
    </div>
  );
}

function BroadcastSegment({ form, setForm, onSend, sending }) {
  return (
    <div className="bg-white border border-stone-200 p-6 h-fit">
      <div className="font-serif text-xl text-ccc-plum mb-4">Segment</div>
      <label className="text-xs uppercase tracking-wider text-stone-500 mb-1 block">Tag</label>
      <input placeholder="e.g. Issue-sanctity-of-life" value={form.segment} onChange={e => setForm({...form, segment: e.target.value})}
        className="w-full px-3 py-2 border border-stone-300 mb-3"/>
      <label className="text-xs uppercase tracking-wider text-stone-500 mb-1 block">Diocese</label>
      <select value={form.diocese} onChange={e => setForm({...form, diocese: e.target.value})}
        className="w-full px-3 py-2 border border-stone-300 mb-6">
        <option value="">All dioceses</option>
        <option>Denver</option><option>Colorado Springs</option><option>Pueblo</option>
      </select>
      <button onClick={onSend} disabled={sending} data-testid="broadcast-send"
        className="w-full bg-ccc-purple hover:bg-ccc-purple-dark text-white py-3 uppercase text-sm tracking-wider font-semibold disabled:opacity-60">
        {sending ? "Sending..." : "Send Broadcast"}
      </button>
    </div>
  );
}

function BroadcastHistory({ history }) {
  return (
    <div className="bg-white border border-stone-200 overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-stone-50 border-b border-stone-200">
          <tr><th className="text-left px-4 py-2">Sent</th><th className="text-left px-4 py-2">Subject</th><th className="text-left px-4 py-2">Segment</th><th className="text-right px-4 py-2">Recipients</th></tr>
        </thead>
        <tbody>
          {history.map(b => (
            <tr key={b.id} className="border-b border-stone-100">
              <td className="px-4 py-2 text-stone-500">{new Date(b.created_at).toLocaleString()}</td>
              <td className="px-4 py-2">{b.subject}</td>
              <td className="px-4 py-2 text-stone-600">{b.segment || b.diocese || "All"}</td>
              <td className="px-4 py-2 text-right">{b.recipient_count}</td>
            </tr>
          ))}
          {history.length === 0 && <tr><td colSpan={4} className="text-center text-stone-500 py-8">No broadcasts yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

export default function Emails() {
  const [form, setForm] = useState({ subject: "", html: "", segment: "", diocese: "", subject_b: "", ab_enabled: false, template_id: null });
  const [history, setHistory] = useState([]);
  const [sending, setSending] = useState(false);
  const [automations, setAutomations] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [savedTemplates, setSavedTemplates] = useState([]);

  const load = useCallback(() => adminBroadcasts().then(setHistory), []);
  const loadTemplates = useCallback(
    () => adminListTemplates().then(r => setSavedTemplates(r.templates || [])).catch(() => {}),
    [],
  );
  const loadAutomations = useCallback(
    () => api.get("/admin/automations/status").then(r => setAutomations(r.data)).catch(() => {}),
    [],
  );
  const loadAnalytics = useCallback(
    () => api.get("/admin/automations/analytics").then(r => setAnalytics(r.data)).catch(() => {}),
    [],
  );

  useEffect(() => {
    load(); loadAutomations(); loadAnalytics(); loadTemplates();
  }, [load, loadAutomations, loadAnalytics, loadTemplates]);

  const runJob = async (job) => {
    try {
      const r = await api.post(`/admin/automations/run/${job}`);
      toast.success(`${job}: ${JSON.stringify(r.data)}`);
      loadAutomations();
    } catch { toast.error("Job failed"); }
  };

  const send = async () => {
    if (!form.subject || !form.html) return toast.error("Subject and body required");
    if (form.ab_enabled && !form.subject_b?.trim()) return toast.error("Subject B required for A/B test");
    const label = form.ab_enabled ? "A/B split broadcast" : "broadcast";
    if (!window.confirm(`Send this ${label} now?`)) return;
    setSending(true);
    try {
      const body = {
        subject: form.subject, html: form.html,
        segment: form.segment || null, diocese: form.diocese || null,
        subject_b: form.ab_enabled ? form.subject_b.trim() : null,
        template_id: form.template_id || null,
      };
      const r = await adminBroadcast(body);
      toast.success(r.ab_test
        ? `A/B sent — A:${r.sent_a} B:${r.sent_b} (${r.sent} total)`
        : `Sent to ${r.sent} recipient${r.sent !== 1 ? "s" : ""}`);
      setForm({ subject: "", html: "", segment: "", diocese: "", subject_b: "", ab_enabled: false, template_id: null });
      load();
    } catch { toast.error("Send failed"); }
    finally { setSending(false); }
  };

  return (
    <div data-testid="admin-emails">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-wider text-ccc-purple">Communications</div>
        <h1 className="font-serif text-4xl text-ccc-plum">Email Broadcast.</h1>
      </div>

      <AnalyticsPanel data={analytics}/>
      <AutomationsPanel data={automations} onRun={runJob}/>
      <HomilyDigestPanel/>

      <div className="grid lg:grid-cols-3 gap-6">
        <BroadcastComposer form={form} setForm={setForm} savedTemplates={savedTemplates}/>
        <BroadcastSegment form={form} setForm={setForm} onSend={send} sending={sending}/>
      </div>

      <div className="mt-10">
        <h2 className="font-serif text-2xl text-ccc-plum mb-4">Recent broadcasts</h2>
        <BroadcastHistory history={history}/>
      </div>
    </div>
  );
}
